<script>
  import { onMount } from "svelte";
  import { CURRENT_TERMS } from "./termsContent.js";
  import { hasAcceptedTerms, acceptTerms } from "./termsApi.js";
  import { t } from "../i18n/i18n.svelte.js";
  import { brand } from "../../brands/index.js";

  // Port of src/user/modules/info_elements/new-terms-modal.tsx — a blocking gate shown until the
  // signed-in user accepts the current Terms version. It cannot be dismissed: no backdrop close, no
  // Escape, no close button (React used a Swal with allowOutsideClick/allowEscapeKey false).
  //
  // A failing acceptance CHECK fails open (the user gets in) — an outage must not lock everyone out.
  // A failing accept SUBMIT keeps the gate up and shows the error, so acceptance is never assumed.

  let open = $state(false);
  let busy = $state(false);
  let error = $state(null);

  onMount(async () => {
    try {
      open = !(await hasAcceptedTerms(CURRENT_TERMS.version));
    } catch {
      open = false;
    }
  });

  async function accept() {
    busy = true;
    error = null;
    try {
      await acceptTerms(CURRENT_TERMS.version);
      open = false;
    } catch (e) {
      error = e.message || String(e);
    } finally {
      busy = false;
    }
  }
</script>

{#if open}
  <div class="terms-backdrop">
    <div class="terms-dialog" role="dialog" aria-modal="true" aria-label="Terms and Conditions">
      <!-- The framing React wrapped the document in (new-terms-modal.tsx:49-54). Without it the
           gate throws 45 kB of legal text at the user with no explanation of why it appeared. -->
      <div class="terms-intro">
        <h2>{t("terms_greeting")}</h2>
        <p>{t("terms_intro_updated")}</p>
        <p>{t("terms_intro_review")}</p>
      </div>
      <div class="terms-body">
        <!-- Trusted, repo-authored document (termsContent.js is generated from the signed .docx). -->
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html CURRENT_TERMS.html}
      </div>
      <!-- React hard-coded info@bitcashier.io here, so the DynamoPayments build told their
           customers to email Bitcashier; the address comes from the active brand instead. -->
      <p class="terms-contact">{t("terms_questions", { email: brand.contact.email })}</p>
      {#if error}
        <div class="error-box">{error}</div>
      {/if}
      <div class="terms-actions">
        <button class="btn" onclick={accept} disabled={busy}>
          {busy ? t("loading") : t("accept")}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .terms-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 20, 27, 0.6);
    display: grid;
    place-items: center;
    z-index: 100;
    padding: 20px;
  }
  .terms-dialog {
    background: var(--card-bg);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: var(--shadow);
    width: min(48rem, 100%);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }
  .terms-intro {
    padding: 24px 24px 0;
  }
  .terms-intro h2 {
    font-size: 16px;
    margin: 0 0 10px;
  }
  .terms-intro p {
    margin: 0 0 8px;
    font-size: 13px;
    line-height: 1.55;
    color: var(--muted);
  }
  .terms-contact {
    margin: 0;
    padding: 0 24px 4px;
    font-size: 13px;
    color: var(--muted);
  }
  .terms-body {
    overflow-y: auto;
    padding: 24px;
    font-size: 13px;
    line-height: 1.55;
  }
  .terms-actions {
    padding: 14px 24px;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: flex-end;
  }

  /* Styling for the generated document markup (scoped here, as React scoped it to its container). */
  .terms-body :global(.doc-title) {
    text-align: center;
    margin-bottom: 20px;
  }
  .terms-body :global(.doc-org) {
    font-weight: 700;
  }
  .terms-body :global(.doc-name) {
    font-size: 18px;
    font-weight: 700;
    margin: 6px 0;
  }
  .terms-body :global(.doc-version) {
    color: var(--muted);
    font-size: 12px;
  }
  .terms-body :global(h1) {
    font-size: 16px;
    margin: 22px 0 8px;
  }
  .terms-body :global(h2) {
    font-size: 14px;
    margin: 18px 0 6px;
  }
  .terms-body :global(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
  }
  .terms-body :global(td),
  .terms-body :global(th) {
    border: 1px solid var(--border);
    padding: 8px 10px;
    vertical-align: top;
    white-space: normal;
  }
  .terms-body :global(p) {
    margin: 8px 0;
  }
</style>
